import Tabbable from 'tabbable'
import Component from '../../lib/component'
import { RootComponent } from '../../main'

export default class ExpandToggleComponent extends Component {
    static selector () {
        return '[data-expand]'
    }

    constructor (opts) {
        super(opts)

        this.expandedFullScreenTabbableElZindex = 6

        this.render()
        this.listen()
    }

    listen () {
        this.el
            .addEventListener('click', this.onToggle.bind(this))

        if (this.el.getAttribute('role') === 'button'
                && !this.el.getAttribute('tabindex'))
            this.el.setAttribute('tabindex', 0)

        if (this.el.getAttribute('role') === 'button')
            this.el
                .addEventListener('keydown', e =>
                    e.keyCode === 32
                            || (e.key || e.keyIdentifier || '')
                                .match(/^(enter)$/i)
                        ? this.onToggle(e)
                        : null)

        document.addEventListener('click',
            this.onDocumentClick.bind(this))

        document.addEventListener('keyup',
            this.onDocumentClick.bind(this))

        document.addEventListener('keydown',
            this.onDocumentKeydown.bind(this))

        this.on('resize', () => this.onResize())

        if (this.props.expandedFullscreen
                && ('MutationObserver' in window))
            (new window.MutationObserver(() => this.isExpanded()
                        && document.documentElement
                            .getAttribute('data-expanded-fullscreen')
                                !== this.props.expand
                ? this.render()
                : null))
                .observe(document.documentElement, {
                    attributes: true
                })

        if (this.getFeature('touch')
                || !this.getMouseoverEl())
            return

        this.getMouseoverEl()
            .addEventListener('mouseover', () => !this.isExpanded()
                    && this.isToggleVisible()
                ? this.el.click()
                : null)

        this.getMouseoverEl()
            .addEventListener('mouseout', () => this.isExpanded()
                    && this.isToggleVisible()
                ? this.el.click()
                : null)
    }

    onToggle (e) {
        e.preventDefault()

        const scrollToTop = this.props.scrollToTop === 'true'

        const scrollToEl = this.props.scrollToEl
            ? document.querySelector(this.props.scrollToEl)
            : null

        if (this.isExpanded()
                && this.isExpandFullscreen()) {
            const isFullscreenExpandToggle = e.target
                .getAttribute('data-expanded-fullscreen')

            if (!isFullscreenExpandToggle
                    || e.target === this.el
                    || e.target.getAttribute('data-expand')
                        === this.props.expand) {
                RootComponent.emit('scroll:enable')

                document.documentElement
                    .removeAttribute('data-expanded-fullscreen')
            }
        }

        if (!this.isExpanded()
                && this.isExpandFullscreen()
                && !(scrollToTop || scrollToEl)) {
            document.documentElement
                .setAttribute('data-expanded-fullscreen',
                    this.props.expand)

            RootComponent.emit('scroll:disable')
        }

        if ((!scrollToTop && !scrollToEl)
                || this.isExpanded()
                || (RootComponent.Scroller || {}).scrollingTo) {
            this.render()

            if (this.isExpandFullscreen()
                    && !this.isExpandedVisible()) {
                const btns = this.getExpandToggles()
                    .filter(el => el !== this.el
                        && this.isElementComputedStyleVisible(el))

                const btn = btns[0] || (this.isElementComputedStyleVisible(this.el)
                    ? this.el
                    : null)

                if (btn && btn !== document.activeElement)
                    btn.focus()
            }

            return
        }

        const top = scrollToEl
            ? scrollToEl.getBoundingClientRect().top
                + (RootComponent.Scroller.state.get('scrollY') || 0)
                + Number(this.props.scrollOffset || '0')
            : 0

        Promise
            .resolve(RootComponent.Scroller.scrollTo({ y: top }, 0.25))
            .then(() => {
                if (this.isExpandFullscreen()) {
                    document.documentElement
                        .setAttribute('data-expanded-fullscreen',
                            this.props.expand)

                    RootComponent.emit('scroll:disable')
                }

                this.render()
            })
    }

    onDocumentClick (e) {
        if (!this.isExpandedVisible()
                || (this.isExpandFullscreen()
                    && (e.key || e.keyIdentifier || '')
                        .match(/^arrow/i)))
            return

        const controlsEl = this.getControlsEl()

        let isClose = !window.getComputedStyle(controlsEl).position
            .match(/(relative|static)/i)
            && !controlsEl.contains(e.target)
            && controlsEl.id !== e.target.getAttribute('data-expand')

        const parentEl = Array.from((RootComponent.el || {}).children)
            .filter(el => el.contains(controlsEl))[0]

        if (isClose
                && this.isExpandFullscreen()
                && e.target !== document.body
                && (e.target.contains(this.el)
                    || (e.type !== 'click'
                        && parentEl
                        && parentEl.contains(e.target))
                    || (this.getExpandedFullscreenTabbableEl()
                        && this.getExpandedFullscreenTabbableEl()
                            .contains(e.target))
                    || (e.target.classList.contains('autocomplete-result')
                        && controlsEl.querySelector('.autocomplete'))))
            isClose = false

        if (!isClose)
            return

        const isFullscreenExpandToggle = e.target
            .getAttribute('data-expanded-fullscreen')

        const isFullscreenClose = !isFullscreenExpandToggle
            || e.target === this.el
            || e.target.getAttribute('data-expand')
                === this.props.expand

        if (this.isExpandFullscreen()
                && isFullscreenClose) {
            RootComponent.emit('scroll:enable')

            document.documentElement
                .removeAttribute('data-expanded-fullscreen')
        }

        this.render()

        if (!this.isElementComputedStyleVisible(this.el)
                || (this.getExpandedFullscreenTabbableEl()
                    && this.getExpandedFullscreenTabbableEl()
                        .contains(document.activeElement)
                    && !this.getControlsEl()
                        .contains(document.activeElement))
                || (this.isExpandFullscreen()
                    && !isFullscreenClose))
            return

        this.el.focus()
    }

    onDocumentKeydown (e) {
        const key = (e.key || e.keyIdentifier || '')

        if (!key || !this.isExpandedVisible())
            return

        const fullscreenTabbableEl = this.getExpandedFullscreenTabbableEl()

        if (key.match(/escape/i)) {
            if (this.isExpandFullscreen()) {
                RootComponent.emit('scroll:enable')

                document.documentElement
                    .removeAttribute('data-expanded-fullscreen')
            }

            this.render()

            if (this.getControlsEl().contains(document.activeElement)) {
                const btns = this.getExpandToggles()
                    .filter(el => el !== this.el
                        && this.isElementComputedStyleVisible(el))

                const btn = btns[0] || (this.isElementComputedStyleVisible(this.el)
                    ? this.el
                    : null)

                if (btn && btn !== document.activeElement)
                    btn.focus()

                return
            }
        }

        if (!this.isExpandFullscreen())
            return

        if (!key.match(/tab/i))
            return

        const tabbableEl = fullscreenTabbableEl || this.getControlsEl()

        if (!tabbableEl)
            return

        let activeEl

        const els = (Tabbable(tabbableEl) || [])
            .filter((el) => {
                if (el.type !== 'radio'
                        || !el.name)
                    return el

                const radios = Array
                    .from(tabbableEl.querySelectorAll(`[name="${el.name}"]`))

                const checked = tabbableEl
                    .querySelector(`[name="${el.name}"]:checked`)

                return (checked === el)
                    || (!checked && radios.indexOf(el) === 0)
            })

        if (e.shiftKey
                && els.indexOf(e.target) === 0)
            activeEl = els[els.length - 1]

        if (!e.shiftKey
                && els.indexOf(e.target) === els.length - 1)
            activeEl = els[0]

        if (!activeEl)
            return

        e.preventDefault()
        e.stopPropagation()

        activeEl.focus()
    }

    onResize () {
        this.renderOffCanvasControlsEl()

        if (!this.isExpandFullscreen()
                || document.documentElement
                    .getAttribute('data-expanded-fullscreen')
                        !== this.props.expand)
            return

        if (this.isExpanded()
                && (!this.isExpandedVisible()
                    || (this.props.scrollToTop
                        && this.isElementComputedStyleVisible(this.el)
                        && RootComponent.Scroller.state.get('scrollY'))))
            return this.el.click()
    }

    isToggleVisible () {
        return Boolean(this.getExpandToggles()
            .filter(el => this.isElementComputedStyleVisible(el))
            .length)
    }

    isExpanded () {
        return this.getControlsEl()
            && this.getControlsEl().getAttribute('data-expanded') === 'true'
    }

    isExpandedVisible () {
        return this.isExpanded()
            && this.isElementComputedStyleVisible(this.getControlsEl())
            && this.isToggleVisible()
    }

    isExpandFullscreen () {
        return this.props.expandedFullscreen === 'true'
    }

    getMouseoverEl () {
        return (this.props || {}).expandOnMouseover
            ? document.querySelector(`${this.props.expandOnMouseover}`)
            : null
    }

    getControlsEl () {
        return document
            .querySelector(`#${this.el.getAttribute('data-expand')}`)
    }

    getExpandedFullscreenFocusEl () {
        if (!this.isExpandFullscreen())
            return null

        const visibleEl = this.getExpandedFullscreenTabbableEl()
            || this.getControlsEl()

        return visibleEl
            .querySelector(this.props.expandedFullscreenFocus
                || '[data-expanded-fullscreen-focus="true"]')
    }

    getExpandedFullscreenTabbableEl () {
        return this.isExpandFullscreen()
                && this.props.expandedFullscreenTabbableEl
            ? document.querySelector(`${
                this.props.expandedFullscreenTabbableEl}`)
            : null
    }

    getExpandToggles () {
        return Array.from(document
            .querySelectorAll(`[data-expand="${this.props.expand}"]`))
    }

    setFullscreenAriaHidden () {
        if (!this.isExpandFullscreen())
            return

        const visibleEl = this.getExpandedFullscreenTabbableEl()
            || this.getControlsEl()

        if (this.isExpandedVisible()
                && !visibleEl.getAttribute('role')) {
            visibleEl
                .setAttribute('role', 'dialog')

            visibleEl
                .setAttribute('aria-modal', true)

            if (this.props.expandedFullscreenDialogLabel) {
                if (visibleEl.getAttribute('aria-label'))
                    visibleEl
                        .setAttribute('data-aria-label-restore',
                            visibleEl.getAttribute('aria-label'))

                visibleEl
                    .setAttribute('aria-label',
                        this.props.expandedFullscreenDialogLabel)
            }
        }

        if (!this.isExpandedVisible()
                && visibleEl.getAttribute('role') === 'dialog') {
            visibleEl.removeAttribute('role')
            visibleEl.removeAttribute('aria-modal')

            if (this.props.expandedFullscreenDialogLabel) {
                visibleEl.removeAttribute('aria-label')

                if (visibleEl.getAttribute('data-aria-label-restore'))
                    visibleEl
                        .setAttribute('aria-label',
                            visibleEl.getAttribute('data-aria-label-restore'))

                visibleEl.removeAttribute('data-aria-label-restore')
            }
        }

        if (!this.isExpandedVisible())
            return Array.from(document
                .querySelectorAll('[data-aria-hidden-reset]'))
                .forEach((el) => {
                    el.removeAttribute('data-aria-hidden-reset')
                    el.removeAttribute('aria-hidden')
                    el.removeAttribute('inert')
                })

        let parentEl = visibleEl.parentElement

        while (parentEl !== document.documentElement) {
            Array.from(parentEl.children)
                .filter(el => visibleEl !== el
                    && !el.contains(visibleEl)
                    && !el.tagName.match(/^(script|styles)$/i)
                    && el.getAttribute('aria-hidden') !== 'true')
                .forEach(el => {
                    el.setAttribute('data-aria-hidden-reset', true)
                    el.setAttribute('aria-hidden', true)
                    el.setAttribute('inert', true)
                })

            parentEl = parentEl.parentElement
        }

        if (!this.getBrowser('os').match(/android/i))
            return

        (Tabbable(document.body) || [])
            .filter(el => visibleEl !== el
                && !visibleEl.contains(el))
            .forEach(el => {
                el.setAttribute('data-aria-hidden-reset', true)
                el.setAttribute('aria-hidden', true)
            })
    }

    renderLabels (el = this.el) {
        let label

        if (this.isExpanded() && el.getAttribute('data-label-expanded'))
            label = el.getAttribute('data-label-expanded')
        else if (!this.isExpanded() && el.getAttribute('data-label'))
            label = el.getAttribute('data-label')

        if (label)
            el.setAttribute('aria-label', label)
    }

    renderOffCanvasControlsEl () {
        if (!this.getExpandedFullscreenTabbableEl()
                || !this.getControlsEl().className
                    .match(/off-canvas-(top|left|right)/))
            return

        if (!this.isElementComputedStyleVisible(this.el)) {
            if (!this.isToggleVisible()
                    && this.getControlsEl().style.top)
                this.getControlsEl().style.top = ''

            return
        }

        const tabbableElHeight = this.getExpandedFullscreenTabbableEl()
            .clientHeight
            ? `${this.getExpandedFullscreenTabbableEl()
                .clientHeight}px`
            : '0px'

        const maxHeight = `calc(100vh - ${tabbableElHeight})`

        if (this.getControlsEl().style.top !== tabbableElHeight)
            this.getControlsEl().style.top = tabbableElHeight

        if (this.getControlsEl().style.maxHeight !== maxHeight)
            this.getControlsEl().style.maxHeight = maxHeight
    }

    render () {
        if (!this.getControlsEl())
            return

        if (!this.getControlsEl().classList.contains(this.getBrowser('name')))
            this.getControlsEl().classList.add(this.getBrowser('name'))

        if (!this.getControlsEl().classList.contains(this.getBrowser('os')))
            this.getControlsEl().classList.add(this.getBrowser('os'))

        if (this.el.tagName === 'A') {
            if (this.el.getAttribute('href'))
                this.el.removeAttribute('href')

            if (this.el.getAttribute('role') !== 'button')
                this.el.setAttribute('role', 'button')
        }

        this.renderOffCanvasControlsEl()

        const isExpanded = !this.el.getAttribute('aria-controls')
            ? this.getControlsEl()
                .getAttribute('data-expanded') === 'true'
            : !this.isExpanded()

        const controls = this.el.getAttribute('data-expand')

        if (!this.el.getAttribute('aria-controls'))
            this.el.setAttribute('aria-controls', controls)

        if (this.isExpandFullscreen()
                && this.getExpandedFullscreenTabbableEl())
            setTimeout(() => {
                const fullscreenEl = document.documentElement
                    .getAttribute('data-expanded-fullscreen')

                if (fullscreenEl
                        && fullscreenEl !== controls)
                    return

                const isExpandedVisible = this.isExpandedVisible()

                if ((isExpandedVisible
                            && !this.getExpandedFullscreenTabbableEl()
                                .style.zIndex)
                        || (!isExpandedVisible
                            && this.getExpandedFullscreenTabbableEl()
                                .style.zIndex))
                    this.getExpandedFullscreenTabbableEl().style.zIndex
                        = isExpandedVisible
                            ? this.expandedFullScreenTabbableElZindex
                            : ''
            }, isExpanded
                ? 0
                : 600)

        this.el.setAttribute('aria-expanded', isExpanded)

        this.getControlsEl()
            .setAttribute('data-expanded', isExpanded)

        if (this.getMouseoverEl())
            this.getMouseoverEl()
                .setAttribute('data-expanded', isExpanded)

        if (this.isExpandFullscreen()
                && this.el.parentElement
                && window.getComputedStyle(this.el.parentElement).position
                    === 'fixed')
            this.el.parentElement
                .setAttribute('data-expanded-child', isExpanded)

        this.renderLabels()

        this.getExpandToggles()
            .filter(el => el !== this.el)
            .forEach(el => {
                if (el.getAttribute('aria-expanded')
                        !== String(this.isExpanded()))
                    el.setAttribute('aria-expanded', this.isExpanded())

                this.renderLabels(el)
            })

        this.setFullscreenAriaHidden()

        const focusEl = this.getExpandedFullscreenFocusEl()

        if (!isExpanded
                || !this.isExpandFullscreen()
                || !focusEl)
            return

        focusEl.focus()
    }
}
